import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import {
    formatStoreyLabel,
    getActiveStorey
} from 'app/modules/wallforming/components/scenes/objects/building/utils/storeyUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { useIsStandaloneMode } from 'shared/hooks/useIsStandaloneMode';

const screenWidth = window.screen.width;

/** Lets stand-alone users pick the storey to form; only shown for models with more than one storey. */
export const StoreyFilterButton = () => {
    const { t } = useTranslation();
    const isStandaloneMode = useIsStandaloneMode();
    const { buildingStoreys, selectedStoreyGuid, setSelectedStoreyGuid } = usePlacementStore(
        useShallow((state) => ({
            buildingStoreys: state.buildingStoreys,
            selectedStoreyGuid: state.selectedStoreyGuid,
            setSelectedStoreyGuid: state.setSelectedStoreyGuid
        }))
    );

    const storeys = useMemo(() => [...buildingStoreys].sort((a, b) => b.elevation - a.elevation), [buildingStoreys]);
    const activeStorey = getActiveStorey(buildingStoreys, selectedStoreyGuid);

    if (!isStandaloneMode || storeys.length < 2) {
        return null;
    }

    const checkIcon = (isActive: boolean) => (
        <Icon
            className="action-menu-entry-decoration action-menu-entry-decoration-icon"
            icon="check"
            style={{ visibility: isActive ? 'visible' : 'hidden' }}
        />
    );

    return (
        <Menu offset={4} position="right-start" shadow="md" withinPortal>
            <Menu.Target>
                <Tooltip
                    label={activeStorey ? formatStoreyLabel(activeStorey) : t('labelAllStoreys')}
                    position="right"
                    variant={variants.tooltip.right}
                >
                    <ActionIcon
                        aria-label={t('labelStorey')}
                        className={`${activeStorey ? '' : 'bg-background-gray-ultra-light hover:bg-background-gray!'} ${
                            screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'
                        }`}
                        data-testid="storey-filter-button"
                        size="lg"
                        variant={activeStorey ? variants.actionIcon.activated : undefined}
                    >
                        layers
                    </ActionIcon>
                </Tooltip>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Label className="min-w-60">{t('labelStorey')}</Menu.Label>
                <Menu.Divider />
                <Menu.Item leftSection={checkIcon(activeStorey === null)} onClick={() => setSelectedStoreyGuid(null)}>
                    {t('labelAllStoreys')}
                </Menu.Item>
                {storeys.map((storey) => (
                    <Menu.Item
                        key={storey.storeyGuid}
                        leftSection={checkIcon(activeStorey?.storeyGuid === storey.storeyGuid)}
                        onClick={() => setSelectedStoreyGuid(storey.storeyGuid)}
                    >
                        {formatStoreyLabel(storey)}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};
